import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import ConfirmationPopup from "../../packages/ConfirmationPopup";
import WPTertiaryButton from "../../packages/WPTertiaryButton";

import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChanges";

import CIPSpecification from "../../components/CIPSpecification";
import DataAppend from "../../components/DataAppend/DataAppned";
import Header from "../../components/Header";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import PlantChecklist from "../../components/PlantChecklist";
import DefaultCIPInformation from "../defaultCIPInformation";
import AdvancedSettings from "./AdvancedSettings/AdvancedSettings";
import AnalyseDataTrainTabs from "./AnalyseDataTrainTabs";
import ColumnSelection from "./columnSelection";
import EmptyStateContent from "./EmptyStateContent";
import FileSheetSelection from "./FileSheetSelection";
import LeftPanel from "./LeftPanel";
import {
  isTrainReadyForAnalysis,
  mergeSelectedTrainReadiness,
} from "./readiness.utils";
import ReferenceCondition from "./referenceCondition";

import {
  CustomModalButtons,
  DEFAULT_RADIO_COLUMNS,
  FILE_SHEET_MAPPING_COLUMN_TYPES,
  NORMALIZATION_TYPES,
} from "../../constants/global.constants";

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  SettingsIcon,
} from "../../assets/icons";

import Footer from "../../common/footer/Footer";
import CircularLoader from "../../common/styles/components/circularLoader";

import { removeLocalStorageItem } from "../../utils/session";
import {
  extractPlantId,
  getDatasetDetails,
  getSelectedPlantTrainInfo,
  updatePlantDataTrain,
} from "../../utils/Utils";

import { API_URLS } from "../../network/appUrls";
import {
  getPlantDetails,
  getPlantReadiness,
  getProceedToAnalysis,
  getSheetColumns,
} from "../../network/cipAPI";

import {
  storeCleanData,
  storeDefaultInformation,
  storeFileId,
  storePlantData,
  storePlantDetails,
  storeTrainChemicalSelection,
  storeUnits,
  updateSelectedTrain,
} from "../../Store/TemplateStore/template.action";

import "./index.scss";

const basePath = API_URLS.basePath;
const initialMandatoryComponentsStatus = {
  columnMapping: {
    fulfilled: false,
    changed: false,
    error: false,
  },
  referenceConditions: {
    fulfilled: false,
    changed: false,
    error: false,
  },
  defaultCIPInfo: {
    fulfilled: false,
    changed: false,
    error: false,
  },
};

const AnalyseData = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const data = location.state;
  const { selectedTrainFromQuery } = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryTrain = Number(queryParams.get("sel_train"));

    return {
      selectedTrainFromQuery:
        Number.isInteger(queryTrain) && queryTrain > 0 ? queryTrain : null,
    };
  }, [location.search]);
  const showRecurrent = data?.showRecurrent || false;
  const { token, plantData, fileSheetMapping } = useSelector((state) => state);
  const [columnOptions, setColumnOptions] = useState([]);
  const [fileId, setFileId] = useState("");
  const [mappedColumn, setMappedColumn] = useState([]);
  const [config, setConfig] = useState([]);
  const [mappedReference, setMappedReference] = useState([]);
  const [tempReferenceValues, setTempReferenceValues] = useState({});
  const [datasetDetails, setDatasetDetails] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileInfo, setFileInfo] = useState([]);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [, setIsNormalizationColumnFilled] = useState(false);
  const [isFTNormFallbackDialogOpen, setIsFTNormFallbackDialogOpen] =
    useState(false);
  const [normSelectionType, setNormSelectionType] = useState(null);
  const [activeLoadingRequests, setActiveLoadingRequests] = useState(0);
  const [sheetNames, setSheetNames] = useState([]);
  const [totalTrainCount, setTotalTrainCount] = useState(null);
  const [selectedColumnRadioColumn, setSelectedColumnRadioColumn] = useState(
    DEFAULT_RADIO_COLUMNS,
  );
  const plantId = extractPlantId();
  const selectedPlantTrain = getSelectedPlantTrainInfo(plantData, plantId);
  const [selectedTrain, setSelectedTrain] = useState(
    () => selectedTrainFromQuery || selectedPlantTrain || 1,
  );
  const [selectedSheet, setSelectedSheet] = useState();
  const [selectedCIPFileName, setSelectedCIPFileName] = useState(null);
  const [appendFileList, setAppendFileList] = useState([]);
  const [isAppendFile, setIsAppendFile] = useState(null);
  const [appendStatus, setAppendStatus] = useState("idle");
  const [hasRecurrentCIPs, setHasRecurrentCIPs] = useState(false);
  const [ownerUserName, setOwnerUserName] = useState("");
  const [mandatoryComponentsStatus, setMandatoryComponentsStatus] = useState(
    initialMandatoryComponentsStatus,
  );
  const [showPlantChecklist, setShowPlantChecklist] = useState(false);
  const [plantChecklistData, setPlantChecklistData] = useState([]);
  const [plantReadinessStatus, setPlantReadinessStatus] = useState([]);
  const [colMappingType, setColMappingType] = useState(
    FILE_SHEET_MAPPING_COLUMN_TYPES.manual,
  );
  const [tempSelectedTrain, setTempSelectedTrain] = useState(null);

  const setIsLoading = useCallback((isActive) => {
    setActiveLoadingRequests((prevCount) =>
      Boolean(isActive) ? prevCount + 1 : Math.max(0, prevCount - 1),
    );
  }, []);

  const handleSettingsDialogOpen = () => {
    setIsSettingsDialogOpen(true);
  };
  const handleSettingsDialogClose = () => {
    setIsSettingsDialogOpen(false);
  };

  const updatePlantAndNavigate = () => {
    if (!plantData.some((p) => String(p.plant_id) === String(plantId))) {
      dispatch(
        storePlantData([
          ...plantData,
          { plant_id: plantId, sel_train: selectedTrain },
        ]),
      );
    } else {
      dispatch(
        storePlantData(updatePlantDataTrain(plantData, plantId, selectedTrain)),
      );
    }
    navigate(`${basePath}home?plantId=${plantId}`);
  };

  const navigateToPlantSetup = async () => {
    navigate(`${basePath}editPlant?plantId=${plantId}&isEdit=true`);
  };

  const hasUnsavedChanges = useMemo(() => {
    return Object.values(mandatoryComponentsStatus).some(
      (status) => status.changed === true,
    );
  }, [mandatoryComponentsStatus]);

  const trainTabActions = useMemo(
    () => ({
      setIsLoading,
      setFileId,
      setMappedColumn,
      setMappedReference,
      setSelectedTrain,
      setSelectedSheet,
      setSelectedCIPFileName,
      setHasRecurrentCIPs,
      setIsAppendFile,
      setAppendFileList,
      setAppendStatus,
      setNormSelectionType,
      setDatasetDetails,
      setSheetNames,
      setColumnOptions,
      setFileInfo,
      setUploadedFiles,
      setColMappingType,
      setTempSelectedTrain,
    }),
    [],
  );

  const mergedTrainReadiness = useMemo(
    () =>
      mergeSelectedTrainReadiness(
        plantReadinessStatus,
        selectedTrain,
        mandatoryComponentsStatus,
      ),
    [plantReadinessStatus, selectedTrain, mandatoryComponentsStatus],
  );

  const trainReadinessMap = useMemo(
    () =>
      mergedTrainReadiness.reduce((acc, trainStatus) => {
        const trainNumber = Number(trainStatus?.train);
        if (Number.isNaN(trainNumber)) {
          return acc;
        }

        acc[trainNumber] = isTrainReadyForAnalysis(trainStatus);
        return acc;
      }, {}),
    [mergedTrainReadiness],
  );

  const getHasIncompleteComponents = () => {
    setPlantChecklistData(mergedTrainReadiness);

    const multiTrainHasIncompleteComponents = mergedTrainReadiness.some(
      (trainStatus) => !isTrainReadyForAnalysis(trainStatus),
    );

    return multiTrainHasIncompleteComponents;
  };

  const onProceedClick = async () => {
    setIsLoading(true);
    try {
      const hasIncompleteComponents = getHasIncompleteComponents();

      if (hasIncompleteComponents) {
        setShowPlantChecklist(true);
      } else {
        const response = await getProceedToAnalysis(fileId, selectedTrain);
        if (response && response.status === 200) {
          const { norm_applied_type: appliedType } = response.data;
          dispatch(
            storeCleanData({
              cleanData: null,
              cleanDate: null,
            }),
          );
          if (appliedType === NORMALIZATION_TYPES.FT_NORM_FALLBACK) {
            setIsFTNormFallbackDialogOpen(true);
          } else {
            updatePlantAndNavigate();
          }
        } else {
          createNotification(
            ERROR_NOTIFICATION,
            response.data.detail.error,
            "",
          );
        }
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFTNormFallbackClose = (action) => {
    setIsFTNormFallbackDialogOpen(false);
    if (action === CustomModalButtons.CONFIRM) {
      updatePlantAndNavigate();
    }
  };

  const getPlantDetail = async () => {
    setIsLoading(true);
    try {
      const response = await getPlantDetails(plantId, true, selectedTrain);
      if (response && response.data) {
        const {
          file,
          parent_file_name,
          plant_file_id,
          col_mapping,
          sheet_names,
          uploaded_files,
          num_trains,
          bulk_cip_file_name,
          has_recurrent_cips,
          config,
          default_cip,
          ref_condition,
          append_files,
          isAppended,
          setting,
          norm_selection_type,
          owner_username,
          name,
          water_type,
          sel_train,
          chemical_selection,
          col_mapping_type,
        } = response.data;

        dispatch(updateSelectedTrain(sel_train));

        let file_id, sheet_name, min_date, max_date, uploaded_in_blob;
        if (file) {
          ({ file_id, sheet_name, min_date, max_date, uploaded_in_blob } =
            file);
        }

        const fileInfo = {
          file_id,
          sheetName: sheet_name,
          fileName: parent_file_name,
          plant_file_id,
          uploaded_in_blob,
        };
        dispatch(storeUnits(setting));
        dispatch(
          storePlantDetails({
            plantName: name,
            water_type,
          }),
        );
        dispatch(storeFileId(fileInfo));
        setFileInfo(fileInfo);
        setSelectedSheet(sheet_name);
        setFileId(file_id);
        setMappedColumn(col_mapping);
        setAppendFileList(append_files);
        setIsAppendFile(isAppended);
        isAppended && setAppendStatus("success");
        setSheetNames(sheet_names);
        setUploadedFiles(uploaded_files);
        setTotalTrainCount(num_trains);
        setSelectedTrain(sel_train);
        setSelectedCIPFileName(bulk_cip_file_name);
        setHasRecurrentCIPs(has_recurrent_cips);
        setNormSelectionType(norm_selection_type);
        setConfig(config);
        setMappedReference(ref_condition?.[0]);
        setOwnerUserName(owner_username);
        setDatasetDetails(
          getDatasetDetails({
            owner_username,
            min_date,
            max_date,
          }),
        );
        if (default_cip) {
          dispatch(storeDefaultInformation(default_cip));
        }

        if (chemical_selection) {
          dispatch(storeTrainChemicalSelection(chemical_selection));
        }

        if (file_id && uploaded_in_blob) {
          setColumnOptions([]);
          const columns = await getSheetColumns(file_id, sel_train);
          if (columns && columns.data) {
            setColumnOptions(columns.data?.cols);
          }
        }
        setColMappingType(col_mapping_type);

        // reset mandatory components status if selected file deleted
        if (plant_file_id === null) {
          setMandatoryComponentsStatus(initialMandatoryComponentsStatus);
        }
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTrain === undefined || selectedTrain === null) {
      return;
    }

    if (selectedTrainFromQuery === selectedTrain) {
      return;
    }

    const nextParams = new URLSearchParams(location.search);
    nextParams.set("sel_train", String(selectedTrain));
    navigate(
      {
        pathname: location.pathname,
        search: `?${nextParams.toString()}`,
      },
      { replace: true },
    );
  }, [
    selectedTrain,
    selectedTrainFromQuery,
    location.pathname,
    location.search,
    navigate,
  ]);

  const refreshPlantReadinessStatus = useCallback(async () => {
    if (!token) {
      setPlantReadinessStatus([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getPlantReadiness();
      setPlantReadinessStatus(response?.data || []);
    } catch {
      setPlantReadinessStatus([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, setIsLoading]);

  const handleRefreshTrainTabs = () => {
    getPlantDetail();
    refreshPlantReadinessStatus();
  };

  useEffect(() => {
    if (selectedTrain === undefined || selectedTrain === null) {
      setSelectedTrain(1);
    }
  }, [selectedTrain]);

  // Fetch readiness on initial load, train change, or file-sheet mapping change.
  useEffect(() => {
    refreshPlantReadinessStatus();
  }, [selectedTrain, fileSheetMapping, refreshPlantReadinessStatus]);

  // Fetch plant details on initial load or file-sheet mapping change.
  // Train-change refresh is handled in AnalyseDataTrainTabs.
  useEffect(() => {
    if (token) getPlantDetail();
  }, [token, fileSheetMapping]);

  useEffect(() => {
    if (showRecurrent) {
      setHasRecurrentCIPs(true);
    }
  }, [showRecurrent]);

  useEffect(() => {
    if (!fileId) {
      setDatasetDetails(
        getDatasetDetails({
          owner_username: ownerUserName,
        }),
      );
    }
  }, [fileId, ownerUserName]);

  useEffect(() => {
    return () => {
      removeLocalStorageItem("prevTrainConfig");
    };
  }, []);

  useUnsavedChangesBlocker(hasUnsavedChanges);

  const hasUploadedFile =
    Array.isArray(uploadedFiles) && uploadedFiles.length > 0;
  const isReferenceConditionDisabled =
    !fileId || !mandatoryComponentsStatus.columnMapping.fulfilled;

  return (
    <div className="container-main">
      <Header showPlantMenu />
      <div className="main-container analyse-data-main-container">
        <div className="content-container descriptionText font-14 analyse-data-content">
          {activeLoadingRequests > 0 && (
            <div className="circular-loader-overlay">
              <CircularLoader />
            </div>
          )}
          <div
            className={`analyse-data-panels ${
              !hasUploadedFile ? "analyse-data-panels--empty" : ""
            }`}
          >
            <div className="analyse-data-panel analyse-data-panel--left">
              <div className="analyse-data-left-panel-parent">
                <LeftPanel
                  uploadedFiles={uploadedFiles}
                  totalTrainCount={totalTrainCount}
                  selectedTrain={selectedTrain}
                  sheetNames={sheetNames}
                  allTrainsStatus={mergedTrainReadiness}
                  setTempSelectedTrain={setTempSelectedTrain}
                  onRefreshTrainTabs={handleRefreshTrainTabs}
                />
              </div>
            </div>

            <div className="analyse-data-panel analyse-data-panel--right">
              <AnalyseDataTrainTabs
                config={config}
                selectedTrain={selectedTrain}
                hasUnsavedChanges={hasUnsavedChanges}
                trainReadinessMap={trainReadinessMap}
                plantId={plantId}
                tempSelectedTrain={tempSelectedTrain}
                trainTabActions={trainTabActions}
              />
              {hasUploadedFile ? (
                <>
                  <FileSheetSelection
                    fileInfo={fileInfo}
                    uploadedFiles={uploadedFiles}
                    config={config}
                    setDatasetDetails={setDatasetDetails}
                    setSelectedTrain={setSelectedTrain}
                    selectedTrain={selectedTrain}
                    selectedSheet={selectedSheet}
                    sheetNames={sheetNames}
                    setFileId={setFileId}
                    setMappedColumn={setMappedColumn}
                    setColumnOptions={setColumnOptions}
                    setMappedReference={setMappedReference}
                    setSelectedSheet={setSelectedSheet}
                    setSelectedCIPFileName={setSelectedCIPFileName}
                    setHasRecurrentCIPs={setHasRecurrentCIPs}
                    setSheetNames={setSheetNames}
                    setIsLoading={setIsLoading}
                    setIsAppendFile={setIsAppendFile}
                    setAppendFileList={setAppendFileList}
                    setAppendStatus={setAppendStatus}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setNormSelectionType={setNormSelectionType}
                    setColMappingType={setColMappingType}
                    setSelectedRadioColumn={setSelectedColumnRadioColumn}
                    setUploadedFiles={setUploadedFiles}
                    setFileInfo={setFileInfo}
                  />
                  <div className="flex-ai-center dataset-name">
                    <span className="tealText">
                      {t("analyseData.datasetName")}{" "}
                    </span>
                    &nbsp;
                    {datasetDetails}
                  </div>

                  <ColumnSelection
                    columnOptions={columnOptions}
                    setColumnOptions={setColumnOptions}
                    fileId={fileId}
                    mappedColumn={mappedColumn}
                    setMappedColumn={setMappedColumn}
                    mappedReference={mappedReference}
                    setTempReferenceValues={setTempReferenceValues}
                    setDatasetDetails={setDatasetDetails}
                    selectedRadioColumn={selectedColumnRadioColumn}
                    setSelectedRadioColumn={setSelectedColumnRadioColumn}
                    setMappedReference={setMappedReference}
                    setIsNormalizationColumnFilled={
                      setIsNormalizationColumnFilled
                    }
                    config={config}
                    setSelectedTrain={setSelectedTrain}
                    selectedTrain={selectedTrain}
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                    normSelectionType={normSelectionType}
                    ownerUserName={ownerUserName}
                    plantReadinessStatus={plantReadinessStatus}
                    colMappingType={colMappingType}
                    uploadedInBlob={fileInfo?.uploaded_in_blob}
                  />

                  <DataAppend
                    title={t("dataAppend.title")}
                    content={t("dataAppend.content")}
                    buttonText={t("dataAppend.appendData")}
                    linkText={t("dataAppend.linkText")}
                    selectedTrain={selectedTrain}
                    fileId={fileId}
                    fileName={fileInfo?.fileName}
                    setIsLoading={setIsLoading}
                    mappedReference={mappedReference}
                    appendFileList={appendFileList}
                    isAppendFile={isAppendFile}
                    appendStatus={appendStatus}
                    setIsAppendFile={setIsAppendFile}
                    setAppendFileList={setAppendFileList}
                    setAppendStatus={setAppendStatus}
                    setTempReferenceValues={setTempReferenceValues}
                  />

                  <ReferenceCondition
                    fileId={fileId}
                    mappedReference={mappedReference}
                    tempReferenceValues={tempReferenceValues}
                    selectedColumnRadioColumn={selectedColumnRadioColumn}
                    selectedTrain={selectedTrain}
                    isDisabled={isReferenceConditionDisabled}
                    isColumnMappingDone={
                      mandatoryComponentsStatus.columnMapping.fulfilled
                    }
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                  />

                  <DefaultCIPInformation
                    selectedTrain={selectedTrain}
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                    noOfRONFElements={mappedColumn?.sel_element_involved}
                    selectedPass={mappedColumn?.sel_pass}
                    selectedStage={mappedColumn?.sel_stage}
                  />

                  <CIPSpecification
                    selectedTrain={selectedTrain}
                    selectedSheet={selectedSheet}
                    selectedCIPFileName={selectedCIPFileName}
                    hasRecurrentCIPs={hasRecurrentCIPs}
                    shouldScrollToRecurrent={showRecurrent}
                    mappedReference={mappedReference}
                  />
                </>
              ) : (
                <EmptyStateContent />
              )}
            </div>
          </div>
        </div>

        {isSettingsDialogOpen && (
          <AdvancedSettings
            outlierRemoval
            handleClose={handleSettingsDialogClose}
            selectedTrain={selectedTrain}
            cipAndShutdownIdentification
          />
        )}
        {showPlantChecklist && (
          <PlantChecklist
            setShowPlantChecklist={setShowPlantChecklist}
            checkListData={plantChecklistData}
          />
        )}
        {isFTNormFallbackDialogOpen && (
          <ConfirmationPopup
            header={t("analyseData.dataNormalizationForAnalysis")}
            description={t("analyseData.ftNormFallbackMessage")}
            confirmBtn={t("ValueStrings.okay")}
            onClose={handleFTNormFallbackClose}
            hideCancel
            type="info"
            width="500px"
          />
        )}
        <div className="flex-between analyse-data-actions">
          <WPTertiaryButton
            customClass="margin-left-10"
            onClick={handleSettingsDialogOpen}
            label={t("analyseData.settings")}
            disabled={!hasUploadedFile}
            icon={<SettingsIcon />}
            iconPlacement="before"
            size="medium"
            className="margin-right-20"
            variant="outlined"
          />

          <div>
            <WPTertiaryButton
              id="back-to-plant-design-btn"
              label={t("analyseData.backtoPlantDesign")}
              onClick={navigateToPlantSetup}
              icon={<ArrowLeftIcon />}
              iconPlacement="before"
              size="medium"
              className="margin-right-20"
              variant="outlined"
            />
            <WPTertiaryButton
              onClick={onProceedClick}
              label={t("analyseData.proceedtoAnalysis")}
              disabled={!hasUploadedFile}
              icon={<ArrowRightIcon />}
              iconPlacement="after"
              variant="contained"
              type="secondary"
              size="medium"
            />
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AnalyseData;
